import React, { useCallback, useEffect, useState } from 'react';
import styles from './Availability.module.scss';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Button from '../../../components/button/Button';
import Table from '../../../components/table/Table';
import Modal from '../../../components/modal/Modal';
import TextAreaField from '../../../components/textAreaField/TextAreaField';
import CustomDatePicker from '../../../components/customDatePicker/CustomDatePicker';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import Icon from '../../../components/icon/Icon';
import ProfilePicture from '../../../components/profilePicture/ProfilePicture';
import Dropdown from '../../../components/dropdown/Dropdown';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import { useAuth } from '../../../contexts/AuthContext';
import { format } from 'date-fns';

const initialForm = { instructorId: null, startDate: null, endDate: null, reason: '' };

const Availability = () => {
  const { showToast } = useToasts();
  const { role } = useAuth();
  const isProgramDirector = role === 'Program Director';
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [deletingId, setDeletingId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, total: 0, start: 1, end: 1, hasPrev: false, hasNext: false });

  useEffect(() => {
    if (!isProgramDirector) return;
    const fetchInstructors = async () => {
      try {
        const response = await axiosClient.get('users/instructor-management/instructos', { params: { limit: 100, page: 1 } });
        setInstructors(response.data?.instructors || []);
      } catch (error) {
        showToast(error.message, 'error');
      }
    };
    fetchInstructors();
  }, [isProgramDirector, showToast]);

  const fetchAvailability = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const response = await axiosClient.get('/instructors/unavailability', { params: { page, limit: 10 } });
      const data = response.data;
      setAvailability(data?.data || []);
      const totalPages = Math.ceil(data.total / data.limit);
      setPagination({
        current: data.page,
        total: data.total,
        start: (data.page - 1) * data.limit + 1,
        end: Math.min(data.page * data.limit, data.total),
        hasPrev: data.page > 1,
        hasNext: data.page < totalPages,
      });
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchAvailability(1);
  }, [fetchAvailability]);

  const handlePageChange = (page) => {
    fetchAvailability(page);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axiosClient.delete(`/instructors/unavailability/${deletingId}`);
      setDeletingId(null);
      fetchAvailability();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmit = async () => {
    if ((isProgramDirector && !form.instructorId) || !form.startDate || !form.endDate || !form.reason) {
      showToast('All fields are required', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const response = await axiosClient.post('/instructors/unavailability', {
        ...(isProgramDirector && { instructorId: form.instructorId }),
        startDate: format(form.startDate, 'yyyy-MM-dd'),
        endDate: format(form.endDate, 'yyyy-MM-dd'),
        reason: form.reason,
      });
      showToast(response.message, 'success');
      setIsModalOpen(false);
      setForm(initialForm);
      fetchAvailability();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const instructorOptions = instructors.map((i) => ({ value: i.id, label: `${i.firstName} ${i.lastName}` }));

  const columns = [
    ...(isProgramDirector ? [{
      header: 'Instructor',
      render: (row) => (
        <div className={styles.instructorCell}>
          <ProfilePicture
            firstName={row.instructor?.firstName}
            lastName={row.instructor?.lastName}
            profilePicture={row.instructor?.profilePicture}
            size="xs"
          />
          <span>{row.instructor?.firstName} {row.instructor?.lastName}</span>
        </div>
      )
    }] : []),
    {
      header: 'Start Date',
      render: (row) => format(new Date(row.startDate), 'MMM d, yyyy')
    },
    {
      header: 'End Date',
      render: (row) => format(new Date(row.endDate), 'MMM d, yyyy')
    },
    {
      header: 'Reason',
      render: (row) => row.reason || '-'
    },
    {
      header: '',
      render: (row) => (
        <button className={styles.deleteBtn} onClick={() => setDeletingId(row.id)}>
          <Icon name="trash" />
        </button>
      )
    },
  ];

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle label={isProgramDirector ? 'Instructor Availability' : 'My Availability'} />
        <Button
          label="Add Unavailability"
          variant="primary"
          size="small"
          onClick={() => setIsModalOpen(true)}
        />
      </div>
      <div className={styles.container}>
        <Table
          columns={columns}
          data={availability}
          emptyMessage="No unavailability records found."
          pagination={pagination}
          onPageChange={handlePageChange}
        />
      </div>

      {deletingId && (
        <Modal>
          <div className={styles.confirmModal}>
            <h2 className={styles.confirmTitle}>Are you sure you want to delete this record?</h2>
            <div className={styles.confirmButtons}>
              <Button
                label="Delete"
                variant="secondary"
                size="small"
                isLoading={isDeleting}
                onClick={handleDelete}
              />
              <Button
                label="Cancel"
                variant="cancel"
                size="small"
                onClick={() => setDeletingId(null)}
              />
            </div>
          </div>
        </Modal>
      )}

      {isModalOpen && (
        <Modal scrollable overflowVisible>
          <div className={styles.modalContent}>
            {isSaving && <LoadingOverlay />}
            <h2 className={styles.modalTitle}>Add Unavailability</h2>
            {isProgramDirector && (
              <Dropdown
                label="Instructor"
                options={instructorOptions}
                value={form.instructorId}
                onChange={(value) => setForm((prev) => ({ ...prev, instructorId: value }))}
                searchable
              />
            )}
            <div className={styles.row}>
              <CustomDatePicker
                label="Start Date"
                date={form.startDate}
                onChange={(date) => setForm((prev) => ({ ...prev, startDate: date }))}
                maxDate={form.endDate || undefined}
              />
              <CustomDatePicker
                label="End Date"
                date={form.endDate}
                onChange={(date) => setForm((prev) => ({ ...prev, endDate: date }))}
                minDate={form.startDate || undefined}
              />
            </div>
            <TextAreaField
              label="Reason"
              value={form.reason}
              onChange={(value) => setForm((prev) => ({ ...prev, reason: value }))}
            />
            <div className={styles.modalActions}>
              <Button
                label="Cancel"
                variant="white"
                size="medium"
                onClick={() => { setIsModalOpen(false); setForm(initialForm); }}
              />
              <Button
                label="Save"
                variant="primary"
                size="medium"
                isLoading={isSaving}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Availability;
